import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { Users, Plus, Send, CheckCircle, Clock, User, Loader2 } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import './Performance.css';

function Feedback360({ user, onNavigate, hideHeader = false }) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRequestResponses, setSelectedRequestResponses] = useState([]);
  const [myResponse, setMyResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createRequestLoading, setCreateRequestLoading] = useState(false);
  const [submitResponseLoading, setSubmitResponseLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({ status: '' });
  
  const [newRequest, setNewRequest] = useState({
    employee_id: '',
    title: '',
    description: '',
    period_start: '',
    period_end: '',
    deadline: '',
    evaluator_ids: [],
    relationship_types: {}
  });

  const [responseData, setResponseData] = useState({
    responses: {},
    scores: {},
    overall_feedback: '',
    strengths: '',
    areas_for_improvement: ''
  });

  useEffect(() => {
    loadRequests();
    if (['admin', 'manager'].includes(user?.role)) {
      loadEmployees();
    }
  }, [filters]);

  // Refresh employees (incl. admins for evaluators list) when opening new request modal
  useEffect(() => {
    if (showNewModal && ['admin', 'manager'].includes(user?.role)) {
      loadEmployees();
    }
  }, [showNewModal, user?.role]);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/employees?all=true&active=all&allRoles=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
        if (data.success) {
          const employeesList = data.employees || data.data || [];
          if (Array.isArray(employeesList)) {
            // Mapper les employés pour s'assurer d'avoir first_name et last_name
            const mappedEmployees = employeesList.map(emp => ({
            id: emp.id,
            first_name: emp.firstName || emp.first_name || '',
            last_name: emp.lastName || emp.last_name || '',
            fullName: emp.fullName || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim()
            }));
            setEmployees(mappedEmployees);
          } else {
            setEmployees([]);
          }
        } else {
          setEmployees([]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      setEmployees([]);
    }
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const res = await fetch(`${API_BASE}/api/performance/feedback-360?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(t('performance.error_loading'));
      
      const data = await res.json();
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Erreur chargement feedback 360:', error);
      alert(t('performance.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const loadRequestDetail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/feedback-360/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Erreur lors du chargement');
      
      const data = await res.json();
      setSelectedRequest(data.request);
      setSelectedRequestResponses(data.responses || []);
      
      const myResp = data.responses?.find(r => r.evaluator_id === user?.userId || r.evaluator_id === user?.id);
      setMyResponse(myResp ?? null);
      
      if (myResp) {
        setResponseData({
          responses: (myResp.responses && (typeof myResp.responses === 'string' ? JSON.parse(myResp.responses) : myResp.responses)) || {},
          scores: (myResp.scores && (typeof myResp.scores === 'string' ? JSON.parse(myResp.scores) : myResp.scores)) || {},
          overall_feedback: myResp.overall_feedback || '',
          strengths: myResp.strengths || '',
          areas_for_improvement: myResp.areas_for_improvement || ''
        });
      } else {
        setResponseData({ responses: {}, scores: {}, overall_feedback: '', strengths: '', areas_for_improvement: '' });
      }
      
      setShowResponseModal(true);
    } catch (error) {
      console.error('Erreur chargement détail:', error);
      alert(t('performance.error_loading'));
    }
  };

  const handleCreateRequest = async () => {
    try {
      if (!newRequest.employee_id || !newRequest.title || newRequest.evaluator_ids.length === 0) {
        alert(t('performance.fill_required_select_evaluators'));
        return;
      }
      setCreateRequestLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/feedback-360`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRequest)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('performance.error_creating'));
      }

      setShowNewModal(false);
      setNewRequest({
        employee_id: '',
        title: '',
        description: '',
        period_start: '',
        period_end: '',
        deadline: '',
        evaluator_ids: [],
        relationship_types: {}
      });
      loadRequests();
      alert(t('performance.feedback_request_created_success'));
    } catch (error) {
      console.error('Erreur création feedback:', error);
      alert(error.message || t('performance.error_creating'));
    } finally {
      setCreateRequestLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    try {
      if (!selectedRequest || !myResponse) return;
      setSubmitResponseLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/feedback-360/${selectedRequest.id}/responses/${myResponse.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(responseData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('performance.error_updating'));
      }

      setShowResponseModal(false);
      loadRequests();
      alert(t('performance.feedback_submitted_success'));
    } catch (error) {
      console.error('Erreur soumission feedback:', error);
      alert(error.message || t('performance.error_updating'));
    } finally {
      setSubmitResponseLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} color="#10B981" />;
      case 'in_progress':
        return <Clock size={16} color="#3B82F6" />;
      default:
        return <Clock size={16} color="#6B7280" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('performance.status_pending'),
      in_progress: t('performance.status_in_progress'),
      completed: t('performance.status_completed_label'),
      cancelled: t('performance.status_cancelled')
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className={hideHeader ? '' : 'performance-container'}>
        <div className="loading">{t('performance.loading')}</div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'performance-container'}>
      {!hideHeader && (
      <div className="objectives-header">
        <div>
          <h1><Users size={32} /> {t('performance.feedback360_title')}</h1>
          <p>{t('performance.feedback360_subtitle')}</p>
        </div>
        {['admin', 'manager'].includes(user?.role) && (
          <button className="btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={20} /> {t('performance.new_request')}
          </button>
        )}
      </div>
      )}
      
      {/* Bouton Nouveau (si header masqué) */}
      {hideHeader && user && (user.role === 'admin' || user.role === 'manager') && (
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={() => setShowNewModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} /> {t('performance.new_request')}
          </button>
        </div>
      )}
      
      {/* Filtres */}
      <div className="filters-section">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">{t('performance.all_statuses')}</option>
          <option value="pending">{t('performance.status_pending')}</option>
          <option value="in_progress">{t('performance.status_in_progress')}</option>
          <option value="completed">{t('performance.status_completed_label')}</option>
        </select>
      </div>

      {/* Liste des demandes */}
      <div className="objectives-grid">
        {requests.length === 0 ? (
          <div className="empty-state">
            <Users size={64} color="#9CA3AF" />
            <p>{t('performance.no_feedback_requests')}</p>
          </div>
        ) : (
          requests.map((req) => (
            <div key={req.id} className="objective-card" onClick={() => loadRequestDetail(req.id)} style={{ cursor: 'pointer' }}>
              <div className="objective-header">
                <div>
                  <h3>{req.title}</h3>
                  <p className="objective-meta">
                    {req.employee_first_name} {req.employee_last_name}
                    {req.department && ` • ${req.department}`}
                  </p>
                </div>
                <div className="objective-status">
                  {getStatusIcon(req.status)}
                  <span>{getStatusLabel(req.status)}</span>
                </div>
              </div>

              {req.description && (
                <p className="objective-description">{req.description}</p>
              )}

              <div className="objective-info">
                <div className="info-item">
                  <Users size={16} />
                  <span>{t('performance.responses_count', { count: req.responses_count || 0, total: req.total_evaluators || 0 })}</span>
                </div>
                {req.deadline && (
                  <div className="info-item">
                    <Clock size={16} />
                    <span>{t('performance.deadline_label')} {new Date(req.deadline).toLocaleDateString(navigator.language || 'fr-FR')}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Nouvelle Demande */}
      {showNewModal && (
        <div className="modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('performance.create_feedback_request')}</h2>
            
            <div className="form-group">
              <label>{t('performance.evaluated_employee_label')}</label>
              <select
                value={newRequest.employee_id}
                onChange={(e) => setNewRequest({ ...newRequest, employee_id: e.target.value })}
                required
              >
                <option value="">{t('performance.select_employee')}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('performance.title_label')}</label>
              <input
                type="text"
                value={newRequest.title}
                onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('performance.description_label')}</label>
              <textarea
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('performance.period_start_label')}</label>
                <input
                  type="date"
                  value={newRequest.period_start}
                  onChange={(e) => setNewRequest({ ...newRequest, period_start: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('performance.period_end_label')}</label>
                <input
                  type="date"
                  value={newRequest.period_end}
                  onChange={(e) => setNewRequest({ ...newRequest, period_end: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('performance.deadline_required_label')}</label>
              <input
                type="date"
                value={newRequest.deadline}
                onChange={(e) => setNewRequest({ ...newRequest, deadline: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('performance.evaluators_label')}</label>
              <div className="evaluators-box">
                {employees.filter(emp => emp.id !== newRequest.employee_id).map((emp) => (
                  <label key={emp.id} className="evaluators-box-item">
                    <input
                      type="checkbox"
                      checked={newRequest.evaluator_ids.includes(emp.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewRequest({
                            ...newRequest,
                            evaluator_ids: [...newRequest.evaluator_ids, emp.id],
                            relationship_types: { ...newRequest.relationship_types, [emp.id]: 'peer' }
                          });
                        } else {
                          setNewRequest({
                            ...newRequest,
                            evaluator_ids: newRequest.evaluator_ids.filter(id => id !== emp.id),
                            relationship_types: { ...Object.fromEntries(Object.entries(newRequest.relationship_types).filter(([k]) => k !== emp.id)) }
                          });
                        }
                      }}
                    />
                    <span className="evaluators-box-name">{emp.first_name} {emp.last_name}</span>
                    {newRequest.evaluator_ids.includes(emp.id) && (
                      <select
                        value={newRequest.relationship_types[emp.id] || 'peer'}
                        onChange={(e) => setNewRequest({
                          ...newRequest,
                          relationship_types: { ...newRequest.relationship_types, [emp.id]: e.target.value }
                        })}
                        className="evaluators-box-select"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="manager">{t('performance.relationship_manager')}</option>
                        <option value="peer">{t('performance.relationship_peer')}</option>
                        <option value="subordinate">{t('performance.relationship_subordinate')}</option>
                        <option value="client">{t('performance.relationship_client')}</option>
                      </select>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowNewModal(false)}>
                {t('performance.cancel')}
              </button>
              <button className="btn-primary" onClick={handleCreateRequest} disabled={createRequestLoading}>
                {createRequestLoading ? <><Loader2 size={18} className="spin" /> {t('performance.creating') || 'Creating…'}</> : t('performance.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détail / Réponse Feedback — ouvre pour tous (créateur, évalué, évaluateur) */}
      {showResponseModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => {
          setShowResponseModal(false);
          setSelectedRequest(null);
          setSelectedRequestResponses([]);
          setMyResponse(null);
        }}>
          <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2>{selectedRequest.title}</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
              {t('performance.feedback_for')} {selectedRequest.employee_first_name} {selectedRequest.employee_last_name}
            </p>

            {selectedRequest.description && (
              <p style={{ marginBottom: '16px', color: 'rgba(255,255,255,0.8)' }}>{selectedRequest.description}</p>
            )}
            <div className="objective-info" style={{ marginBottom: '24px' }}>
              {selectedRequest.period_start && selectedRequest.period_end && (
                <div className="info-item">
                  <span>{t('performance.period_start_label')} – {t('performance.period_end_label')}: {new Date(selectedRequest.period_start).toLocaleDateString()} – {new Date(selectedRequest.period_end).toLocaleDateString()}</span>
                </div>
              )}
              {selectedRequest.deadline && (
                <div className="info-item">
                  <Clock size={16} />
                  <span>{t('performance.deadline_label')} {new Date(selectedRequest.deadline).toLocaleDateString()}</span>
                </div>
              )}
              <div className="info-item">
                <Users size={16} />
                <span>{t('performance.responses_count', { count: selectedRequest.responses_count ?? 0, total: selectedRequest.total_evaluators ?? 0 })}</span>
              </div>
            </div>

            {myResponse ? (
              <>
                <div className="form-group">
                  <label>{t('performance.strengths_label')}</label>
                  <textarea
                    value={responseData.strengths}
                    onChange={(e) => setResponseData({ ...responseData, strengths: e.target.value })}
                    rows={4}
                    placeholder={t('performance.strengths_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('performance.areas_improvement_label')}</label>
                  <textarea
                    value={responseData.areas_for_improvement}
                    onChange={(e) => setResponseData({ ...responseData, areas_for_improvement: e.target.value })}
                    rows={4}
                    placeholder={t('performance.areas_improvement_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('performance.overall_feedback_label')}</label>
                  <textarea
                    value={responseData.overall_feedback}
                    onChange={(e) => setResponseData({ ...responseData, overall_feedback: e.target.value })}
                    rows={5}
                    placeholder={t('performance.overall_feedback_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('performance.scores_by_category')}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { key: 'Communication', labelKey: 'category_communication' },
                      { key: 'Leadership', labelKey: 'category_leadership' },
                      { key: 'Collaboration', labelKey: 'category_collaboration' },
                      { key: 'Technical skills', labelKey: 'category_technical_skills' },
                      { key: 'Initiative', labelKey: 'category_initiative' }
                    ].map(({ key, labelKey }) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ minWidth: '150px', fontSize: '14px' }}>{t(`performance.${labelKey}`)}:</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={responseData.scores[key] ?? ''}
                          onChange={(e) => setResponseData({
                            ...responseData,
                            scores: { ...responseData.scores, [key]: parseInt(e.target.value) || 0 }
                          })}
                          style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={() => { setShowResponseModal(false); setSelectedRequest(null); setSelectedRequestResponses([]); setMyResponse(null); }}>
                    {t('performance.cancel')}
                  </button>
                  <button className="btn-primary" onClick={handleSubmitResponse} disabled={myResponse.status === 'submitted' || submitResponseLoading}>
                    {submitResponseLoading ? <><Loader2 size={16} className="spin" /> {t('performance.submitting') || 'Submitting…'}</> : <><Send size={16} /> {myResponse.status === 'submitted' ? t('performance.already_submitted') : t('performance.submit')}</>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px' }}>{t('performance.feedback_received') || 'Feedback reçu'}</h3>
                {selectedRequestResponses.filter(r => r.status === 'submitted').length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>{t('performance.no_feedback_submitted_yet') || 'Aucun feedback soumis pour le moment.'}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                    {selectedRequestResponses.filter(r => r.status === 'submitted').map((resp) => {
                      const scores = typeof resp.scores === 'string' ? (() => { try { return JSON.parse(resp.scores); } catch { return {}; } })() : (resp.scores || {});
                      const categoryKeys = ['Communication', 'Leadership', 'Collaboration', 'Technical skills', 'Initiative'];
                      return (
                        <div key={resp.id} style={{ padding: '16px', background: 'rgba(255,255,255,0.06)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <p style={{ fontWeight: 600, marginBottom: '12px', color: 'rgba(255,255,255,0.95)' }}>
                            {resp.evaluator_first_name} {resp.evaluator_last_name}
                            {resp.relationship_type && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}> ({resp.relationship_type})</span>}
                          </p>
                          {resp.strengths && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>{t('performance.strengths_label')}:</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' }}>{resp.strengths}</p>
                            </div>
                          )}
                          {resp.areas_for_improvement && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>{t('performance.areas_improvement_label')}:</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' }}>{resp.areas_for_improvement}</p>
                            </div>
                          )}
                          {resp.overall_feedback && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>{t('performance.overall_feedback_label')}:</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' }}>{resp.overall_feedback}</p>
                            </div>
                          )}
                          {categoryKeys.some(k => scores[k] != null && scores[k] !== '') && (
                            <div style={{ marginTop: '8px' }}>
                              <strong style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>{t('performance.scores_by_category')}:</strong>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: '6px' }}>
                                {categoryKeys.map((key) => {
                                  const labelKey = { 'Communication': 'category_communication', 'Leadership': 'category_leadership', 'Collaboration': 'category_collaboration', 'Technical skills': 'category_technical_skills', 'Initiative': 'category_initiative' }[key];
                                  const val = scores[key];
                                  if (val == null || val === '') return null;
                                  return <span key={key} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>{t(`performance.${labelKey}`)}: {val}/100</span>;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn-primary" onClick={() => { setShowResponseModal(false); setSelectedRequest(null); setMyResponse(null); setSelectedRequestResponses([]); }}>
                    {t('performance.close')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Feedback360;

